import React, { useState } from "react";
import {
    Card, CardBody, Col, Row, Badge, Button, Spinner,
    Input, Label, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { getResidentData, setResidentData } from "../../helpers/residentauth";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";

const api = new APIClient();

const ResidentProfile: React.FC = () => {
    const [data, setData] = useState<any>(getResidentData());
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Password Modal
    const [passwordModal, setPasswordModal] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    
    // Form data
    const [formData, setFormData] = useState({
        name: data?.name || "",
        email: data?.email || "",
        phone: data?.phone || ""
    });

    // Password data
    const [passwordData, setPasswordData] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const [showOldPwd, setShowOldPwd] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);

    /* ── Enable Edit Mode ── */
    const handleEdit = () => {
        setFormData({
            name: data.name,
            email: data.email,
            phone: data.phone
        });
        setIsEditing(true);
    };

    /* ── Cancel Edit ── */
    const handleCancel = () => {
        setIsEditing(false);
        setFormData({
            name: data.name,
            email: data.email,
            phone: data.phone
        });
    };

    /* ── Save Profile ── */
    const handleSave = async () => {
        // Validation
        if (!formData.name.trim()) {
            toast.warning("Name is required");
            return;
        }
        if (!formData.email.trim()) {
            toast.warning("Email is required");
            return;
        }
        if (formData.phone.length !== 10) {
            toast.warning("Phone must be 10 digits");
            return;
        }

        setSaving(true);
        try {
            const res: any = await api.put("/api/resident/profile", formData);
            
            if (res?.success) {
                // Update local state
                const updatedData = { ...data, ...res.data };
                setData(updatedData);
                
                // Update localStorage
                localStorage.setItem("residentData", JSON.stringify(updatedData));
                
                toast.success("Profile updated! 🎉");
                setIsEditing(false);
            } else {
                toast.error("Update failed");
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to update");
        } finally {
            setSaving(false);
        }
    };

    /* ── Change Password ── */
    const handleChangePassword = async () => {
        // Validation
        if (!passwordData.oldPassword) {
            toast.warning("Enter old password");
            return;
        }
        if (passwordData.newPassword.length < 6) {
            toast.warning("New password must be 6+ characters");
            return;
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.warning("Passwords don't match");
            return;
        }

        setChangingPassword(true);
        try {
            const res: any = await api.put("/api/resident/change-password", {
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword
            });

            if (res?.success) {
                toast.success("Password changed successfully! 🎉");
                setPasswordModal(false);
                setPasswordData({
                    oldPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed");
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <h4 className="fw-bold mb-0">
                    <i className="ri-user-line me-2 text-primary" />
                    My Profile
                </h4>
                
                {!isEditing && (
                    <div className="d-flex gap-2">
                        <Button 
                            color="warning" 
                            outline
                            onClick={() => setPasswordModal(true)}
                        >
                            <i className="ri-lock-line me-1" />
                            Change Password
                        </Button>
                        <Button 
                            color="primary"
                            onClick={handleEdit}
                        >
                            <i className="ri-edit-line me-1" />
                            Edit Profile
                        </Button>
                    </div>
                )}
            </div>

            <Row>
                {/* Left - Profile Card */}
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center" style={{ borderRadius: "12px", background: "var(--bg-card)" }}>
                        <CardBody className="p-4">
                            <div style={{
                                width: "100px", height: "100px", borderRadius: "50%",
                                background: "linear-gradient(135deg, #764ba2, #764ba2)",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                color: "#fff", fontSize: "40px", fontWeight: 700,
                                marginBottom: "16px",
                            }}>
                                {data?.name?.charAt(0).toUpperCase()}
                            </div>
                            <h5 className="fw-bold mb-1" style={{ color: "var(--text-primary)" }}>{data?.name}</h5>
                            <p className="text-muted mb-3">{data?.email}</p>
                            <Badge color={data?.ownerType === "OWNER" ? "info" : "warning"} pill>
                                {data?.ownerType}
                            </Badge>
                            
                            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-color)" }}>
                                <div className="text-muted small">Total Units</div>
                                <div className="fw-bold" style={{ fontSize: 24, color: "#764ba2" }}>
                                    {data?.unit?.length || 0}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* Right - Info Section */}
                <Col md={8}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", background: "var(--bg-card)" }}>
                        <CardBody className="p-4">
                            
                            {/* ─── Personal Information ─── */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="fw-bold mb-0" style={{ color: "var(--text-primary)" }}>
                                    <i className="ri-user-3-line me-2 text-primary" />
                                    Personal Information
                                </h5>
                                {isEditing && (
                                    <Badge color="warning" pill>
                                        <i className="ri-edit-line me-1" />Editing
                                    </Badge>
                                )}
                            </div>

                            {/* ✅ EDIT MODE */}
                            {isEditing ? (
                                <div>
                                    <Row>
                                        <Col md={12} className="mb-3">
                                            <FormGroup>
                                                <Label className="fw-semibold small" style={{ color: "var(--text-primary)" }}>
                                                    Full Name <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="text"
                                                    value={formData.name}
                                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                    placeholder="Enter full name"
                                                    style={{ height: 42, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                                                />
                                            </FormGroup>
                                        </Col>
                                        
                                        <Col md={6} className="mb-3">
                                            <FormGroup>
                                                <Label className="fw-semibold small" style={{ color: "var(--text-primary)" }}>
                                                    Email <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                    placeholder="you@example.com"
                                                    style={{ height: 42, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                                                />
                                            </FormGroup>
                                        </Col>
                                        
                                        <Col md={6} className="mb-3">
                                            <FormGroup>
                                                <Label className="fw-semibold small" style={{ color: "var(--text-primary)" }}>
                                                    Phone <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={e => {
                                                        const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                        setFormData({ ...formData, phone: v });
                                                    }}
                                                    placeholder="10 digit mobile"
                                                    maxLength={10}
                                                    style={{ height: 42, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Save / Cancel Buttons */}
                                    <div className="d-flex gap-2 mt-3">
                                        <Button 
                                            color="success" 
                                            onClick={handleSave}
                                            disabled={saving}
                                            style={{ minWidth: 120 }}
                                        >
                                            {saving ? (
                                                <><Spinner size="sm" className="me-1" />Saving...</>
                                            ) : (
                                                <><i className="ri-check-line me-1" />Save Changes</>
                                            )}
                                        </Button>
                                        <Button 
                                            color="light" 
                                            onClick={handleCancel}
                                            disabled={saving}
                                        >
                                            <i className="ri-close-line me-1" />Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                /* ✅ VIEW MODE */
                                <table className="table table-borderless" style={{ color: "var(--text-primary)" }}>
                                    <tbody>
                                        <tr>
                                            <td className="text-muted" style={{ width: "40%" }}>
                                                <i className="ri-user-line me-2" />Full Name
                                            </td>
                                            <td className="fw-semibold">{data?.name}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                <i className="ri-mail-line me-2" />Email
                                            </td>
                                            <td className="fw-semibold">{data?.email}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                <i className="ri-phone-line me-2" />Phone
                                            </td>
                                            <td className="fw-semibold">{data?.phone}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                <i className="ri-user-settings-line me-2" />Owner Type
                                            </td>
                                            <td>
                                                <Badge color={data?.ownerType === "OWNER" ? "info" : "warning"}>
                                                    {data?.ownerType}
                                                </Badge>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}

                            {/* ─── Unit Details (Always View) ─── */}
                            <h5 className="fw-bold mt-4 mb-3" style={{ color: "var(--text-primary)" }}>
                                <i className="ri-home-4-line me-2 text-primary" />
                                Unit Details
                            </h5>
                            <table className="table table-borderless" style={{ color: "var(--text-primary)" }}>
                                <tbody>
                                    <tr>
                                        <td className="text-muted" style={{ width: "40%" }}>
                                            <i className="ri-building-line me-2" />Total Units
                                        </td>
                                        <td className="fw-semibold text-primary">
                                            <Badge color="primary" pill style={{ fontSize: 13 }}>
                                                {data?.unit?.length || 0} units
                                            </Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted">
                                            <i className="ri-hashtag me-2" />Unit Numbers
                                        </td>
                                        <td>
                                            {data?.unit?.map((u: any, i: number) => (
                                                <Badge 
                                                    key={i} 
                                                    color="info" 
                                                    pill 
                                                    className="me-1 mb-1"
                                                    style={{ fontSize: 12 }}
                                                >
                                                    {u.unitNo}
                                                </Badge>
                                            ))}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ── CHANGE PASSWORD MODAL ── */}
            <Modal isOpen={passwordModal} toggle={() => !changingPassword && setPasswordModal(false)} centered contentClassName="bg-card">
                <ModalHeader toggle={() => !changingPassword && setPasswordModal(false)} className="border-0">
                    <span className="fw-bold" style={{ color: "var(--text-primary)" }}>
                        <i className="ri-lock-line me-2 text-warning" />
                        Change Password
                    </span>
                </ModalHeader>
                <ModalBody>
                    <div style={{
                        background: "rgba(245, 158, 11, 0.1)",
                        padding: "12px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        fontSize: 13,
                        color: "#F59E0B"
                    }}>
                        <i className="ri-information-line me-1" />
                        Use a strong password (at least 6 characters)
                    </div>

                    {/* Old Password */}
                    <FormGroup className="mb-3">
                        <Label className="fw-semibold small" style={{ color: "var(--text-primary)" }}>
                            Current Password <span className="text-danger">*</span>
                        </Label>
                        <div style={{ position: "relative" }}>
                            <Input
                                type={showOldPwd ? "text" : "password"}
                                value={passwordData.oldPassword}
                                onChange={e => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                                placeholder="Enter current password"
                                style={{ height: 42, paddingRight: 40, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowOldPwd(!showOldPwd)}
                                style={{
                                    position: "absolute", right: 12, top: "50%",
                                    transform: "translateY(-50%)", background: "none",
                                    border: "none", color: "var(--text-secondary)"
                                }}
                            >
                                <i className={`ri-${showOldPwd ? "eye-off" : "eye"}-line`} />
                            </button>
                        </div>
                    </FormGroup>

                    {/* New Password */}
                    <FormGroup className="mb-3">
                        <Label className="fw-semibold small" style={{ color: "var(--text-primary)" }}>
                            New Password <span className="text-danger">*</span>
                        </Label>
                        <div style={{ position: "relative" }}>
                            <Input
                                type={showNewPwd ? "text" : "password"}
                                value={passwordData.newPassword}
                                onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                placeholder="Min 6 characters"
                                style={{ height: 42, paddingRight: 40, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPwd(!showNewPwd)}
                                style={{
                                    position: "absolute", right: 12, top: "50%",
                                    transform: "translateY(-50%)", background: "none",
                                    border: "none", color: "var(--text-secondary)"
                                }}
                            >
                                <i className={`ri-${showNewPwd ? "eye-off" : "eye"}-line`} />
                            </button>
                        </div>
                    </FormGroup>

                    {/* Confirm Password */}
                    <FormGroup className="mb-3">
                        <Label className="fw-semibold small" style={{ color: "var(--text-primary)" }}>
                            Confirm New Password <span className="text-danger">*</span>
                        </Label>
                        <div style={{ position: "relative" }}>
                            <Input
                                type={showConfirmPwd ? "text" : "password"}
                                value={passwordData.confirmPassword}
                                onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                placeholder="Re-enter new password"
                                style={{
                                    height: 42,
                                    paddingRight: 40,
                                    background: "var(--bg-item)",
                                    color: "var(--text-primary)",
                                    borderColor: passwordData.confirmPassword && 
                                        passwordData.newPassword !== passwordData.confirmPassword 
                                        ? "#dc2626" : "var(--border-color)"
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                                style={{
                                    position: "absolute", right: 12, top: "50%",
                                    transform: "translateY(-50%)", background: "none",
                                    border: "none", color: "var(--text-secondary)"
                                }}
                            >
                                <i className={`ri-${showConfirmPwd ? "eye-off" : "eye"}-line`} />
                            </button>
                        </div>
                        {passwordData.confirmPassword && 
                         passwordData.newPassword !== passwordData.confirmPassword && (
                            <small className="text-danger">Passwords don't match</small>
                        )}
                    </FormGroup>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button 
                        color="secondary" 
                        onClick={() => {
                            setPasswordModal(false);
                            setPasswordData({
                                oldPassword: "",
                                newPassword: "",
                                confirmPassword: ""
                            });
                        }}
                        disabled={changingPassword}
                    >
                        Cancel
                    </Button>
                    <Button 
                        color="warning" 
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                    >
                        {changingPassword ? (
                            <><Spinner size="sm" className="me-1" />Changing...</>
                        ) : (
                            <><i className="ri-check-line me-1" />Change Password</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ResidentProfile;